import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Calculator, Inbox, CalendarDays, FileText, Settings } from "lucide-react";
import AdminBlogCMS from "./AdminBlogCMS";
import { TOKEN_KEY } from "./AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const ChangePassword = ({ token }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { currentPassword: current, newPassword: next },
        { headers: { Authorization: `Bearer ${token}` } });
      setMsg("Password changed. The old password no longer works.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md border border-ink/10 bg-white p-8" data-testid="change-password-form">
      <h2 className="font-display font-semibold text-xl text-ink">Change password</h2>
      <p className="mt-2 text-sm text-ink/45">After changing, your old password stops working immediately.</p>
      {[
        { l: "Current Password", v: current, s: setCurrent, t: "cp-current" },
        { l: "New Password", v: next, s: setNext, t: "cp-new" },
        { l: "Confirm New Password", v: confirm, s: setConfirm, t: "cp-confirm" },
      ].map((f) => (
        <label key={f.t} className="block mt-6">
          <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">{f.l}</span>
          <input type="password" value={f.v} onChange={(e) => f.s(e.target.value)} required
            data-testid={f.t}
            className="mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors" />
        </label>
      ))}
      {err && <p className="mt-4 text-sm text-red-400" data-testid="cp-error">{err}</p>}
      {msg && <p className="mt-4 text-sm text-skope" data-testid="cp-success">{msg}</p>}
      <button type="submit" disabled={loading}
        data-testid="cp-submit"
        className="mt-8 bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase px-8 py-3.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-50">
        {loading ? "Updating…" : "Update Password"}
      </button>
    </form>
  );
};

const EnquiryCard = ({ e }) => (
  <div className="border border-ink/10 bg-[#f6f6f6] p-6" data-testid={`enquiry-${e.id}`}>
    <div className="flex flex-wrap items-center gap-3 justify-between">
      <div className="flex items-center gap-3">
        <span className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border ${
          e.type === "calculator_lead" ? "border-skope/50 text-skope" : "border-ink/25 text-ink/60"
        }`}>
          {e.type === "calculator_lead" ? "Calculator Lead" : e.type === "schedule_call" ? "Call Request" : "Contact"}
        </span>
        <span className="font-display font-semibold text-ink">{e.name}</span>
      </div>
      <span className="font-mono text-[10px] text-ink/35">{fmtDate(e.created_at)}</span>
    </div>
    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm">
      <span className="text-ink/60">Email: <a href={`mailto:${e.email}`} className="text-skope">{e.email}</a></span>
      {e.phone && <span className="text-ink/60">Phone: <span className="text-ink/85">{e.phone}</span></span>}
      {e.altPhone && <span className="text-ink/60">Alt Phone: <span className="text-ink/85">{e.altPhone}</span></span>}
      {e.city && <span className="text-ink/60">City: <span className="text-ink/85">{e.city}</span></span>}
      {e.brand && <span className="text-ink/60">Brand: <span className="text-ink/85">{e.brand}</span></span>}
      {e.building && <span className="text-ink/60">Building: <span className="text-ink/85">{e.building}</span></span>}
      {e.reason && <span className="text-ink/60">Requirement: <span className="text-ink/85">{e.reason}</span></span>}
      {e.planning && <span className="text-ink/60">Planning: <span className="text-ink/85">{e.planning}</span></span>}
      {e.preferredDate && <span className="text-ink/60">Preferred: <span className="text-skope">{e.preferredDate} {e.preferredTime} {e.timezone}</span></span>}
      {e.timeline && <span className="text-ink/60">Timeline: <span className="text-ink/85">{e.timeline}</span></span>}
    </div>
    {e.message && <p className="mt-3 text-sm text-ink/50 leading-relaxed border-l-2 border-ink/10 pl-4">{e.message}</p>}
    {e.type === "calculator_lead" && e.results && (
      <div className="mt-4 border border-ink/10 bg-white p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 font-mono text-[11px]">
        <span className="text-ink/50">Complexity: <span className="text-skope">{e.results.complexityBand} ({e.results.complexityIndex}/100)</span></span>
        <span className="text-ink/50">Manpower: <span className="text-ink">{e.results.manpower}</span></span>
        <span className="text-ink/50">Business: <span className="text-ink">{e.results.summary?.businessType}</span></span>
        <span className="text-ink/50">Orders/day: <span className="text-ink">{e.results.summary?.dailyOrders}</span></span>
      </div>
    )}
  </div>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [verified, setVerified] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [section, setSection] = useState("enquiries");

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries([]);
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  // verify the stored token server-side before showing anything
  useEffect(() => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setVerified(true))
      .catch(() => logout());
  }, [token, navigate, logout]);

  const load = useCallback(async (t = token, f = filter) => {
    if (!t) return;
    setLoading(true);
    setError("");
    try {
      const params = f !== "all" ? { type: f } : {};
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        headers: { Authorization: `Bearer ${t}` }, params,
      });
      setEnquiries(data.enquiries);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        setError("Could not load enquiries. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, filter, logout]);

  useEffect(() => { if (verified) load(); }, [load, verified]);

  if (!token || !verified) return null;

  const counts = {
    all: enquiries.length,
    contact: enquiries.filter((e) => e.type === "contact").length,
    calculator_lead: enquiries.filter((e) => e.type === "calculator_lead").length,
    schedule_call: enquiries.filter((e) => e.type === "schedule_call").length,
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <header className="border-b border-ink/10 bg-white/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/skope-logo-full.png" alt="Skope Kitchens" className="h-6 w-auto" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-ink/40 uppercase hidden sm:inline">Enquiry Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => load()} className="p-2 text-ink/50 hover:text-skope transition-colors" aria-label="Refresh" data-testid="admin-refresh">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 hover:text-red-400 transition-colors" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex gap-2 mb-8" data-testid="admin-sections">
          {[
            { k: "enquiries", l: "Enquiries", icon: Inbox },
            { k: "blog", l: "Blog & SEO", icon: FileText },
            { k: "settings", l: "Settings", icon: Settings },
          ].map((s) => (
            <button key={s.k} onClick={() => setSection(s.k)}
              className={`flex items-center gap-2 border px-5 py-3 font-mono text-xs tracking-[0.15em] uppercase transition-colors ${
                section === s.k ? "border-skope text-skope bg-skope/10" : "border-ink/15 text-ink/50 hover:text-ink"
              }`}
              data-testid={`section-${s.k}`}>
              <s.icon size={14} /> {s.l}
            </button>
          ))}
        </div>

        {section === "blog" ? (
          <AdminBlogCMS token={token} />
        ) : section === "settings" ? (
          <ChangePassword token={token} />
        ) : (
          <>
        <div className="flex flex-wrap gap-3" data-testid="admin-filters">
          {[
            { key: "all", label: "All", icon: Inbox, count: counts.all },
            { key: "schedule_call", label: "Call Requests", icon: CalendarDays, count: counts.schedule_call },
            { key: "contact", label: "Contact", icon: Mail, count: counts.contact },
            { key: "calculator_lead", label: "Calculator Leads", icon: Calculator, count: counts.calculator_lead },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              data-testid={`filter-${t.key}`}
              className={`flex items-center gap-2 border px-4 py-2.5 font-mono text-[11px] tracking-[0.15em] uppercase transition-colors ${
                filter === t.key ? "border-skope text-skope bg-skope/10" : "border-ink/15 text-ink/50 hover:text-ink"
              }`}
            >
              <t.icon size={13} /> {t.label}
              <span className="text-ink/35">{t.count}</span>
            </button>
          ))}
        </div>

        {error && <p className="mt-6 text-sm text-red-400" data-testid="admin-error">{error}</p>}

        <div className="mt-8 space-y-4" data-testid="enquiry-list">
          {enquiries.length === 0 && !loading && (
            <div className="border border-ink/10 p-14 text-center" data-testid="enquiry-empty">
              <p className="font-display text-ink/60 text-lg">No enquiries yet.</p>
              <p className="mt-2 text-sm text-ink/35">New contact form and calculator submissions will appear here.</p>
            </div>
          )}
          {enquiries.map((e) => <EnquiryCard key={e.id} e={e} />)}
        </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
